import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import classnames from 'classnames';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Async from 'react-select/async';
import type { IReduxState } from '../../../../../configureStore';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IProcess } from '../../../../interfaces/process';
import type { IReactSelectOptions } from '../../../../interfaces/reactSelectOptions';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { fetchJson } from '../../../../../store/fetch';
import { getSelectedLanguage } from '../../../../../translate';
import { Feedback } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { SelectOption } from '../../../../elements/SelectOption';

interface ISelectedProvider {
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
}

interface ICareProviderAndRequesterAndSelectedProvider extends ISelectedProvider {
    draftCareProviderOrganizationId: string | null;
    requesterOrganizationId: string | null;
}

interface IPractitioner {
    absenceEndDate: string | null;
    id: string;
    isActivated: boolean;
    isRequestRecipient: boolean;
    nameFamily?: string;
    nameGiven?: string;
    nameInfix?: string;
}

interface IServiceProviderOrganizationPractitioners {
    city: string;
    id: string;
    isOrganizationRequestRecipient?: boolean;
    name: string;
    practitioners: IPractitioner[];
}

interface IOrganizationPractitionersOptions {
    disabled: boolean;
    label: string;
    practitioners: {
        disabled: boolean;
        label: string;
        value: string;
    }[];
    value: string;
}

interface IGraderSelectorProps {
    careProviderAndRequesterAndSelectedProvider: ICareProviderAndRequesterAndSelectedProvider;
    errors: ValidationErrors;
    handleChange: <K extends keyof ISelectedProvider>(name: K, value: ISelectedProvider[K]) => Promise<void>;
    isExaminationRequest?: boolean;
    procedurePerformerOrganizationId: string;
    process: IProcess | undefined;
    service: IServiceGetOne;
    validateOne: (name: keyof ISelectedProvider) => Promise<void>;
}

export function GraderSelector(props: IGraderSelectorProps) {
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const translate = getSelectedLanguage();

    const graderSelectedByRequester = Boolean(
        props.process &&
            !props.process.isDirectProcedureAdded &&
            props.process.selectedDiagnosticReportProviderOrganizationId
    );
    const [organizationPractitioners, setOrganizationPractitioners] = useState<
        IOrganizationPractitionersOptions[] | undefined
    >();
    const [selectedOption, setSelectedOption] = useState<IReactSelectOptions>();
    const [noGradersOnMount, setNoGradersOnMount] = useState(false);

    useEffect(() => {
        if (props.service.userIsConsumerGraderSelector || props.service.userIsProviderGraderSelector) {
            loadOrganizations(props.service.id, true);
        }
    }, []);

    useEffect(() => {
        if (props.service.userIsConsumerGraderSelector || props.service.userIsProviderGraderSelector) {
            reloadOptions();
        }
    }, [
        props.procedurePerformerOrganizationId,
        props.careProviderAndRequesterAndSelectedProvider.requesterOrganizationId,
        props.careProviderAndRequesterAndSelectedProvider.draftCareProviderOrganizationId,
    ]);

    return (
        <>
            {(props.service.userIsConsumerGraderSelector || props.service.userIsProviderGraderSelector) && (
                <>
                    <HorizontalFormGroup hasErrors={props.errors.has('selectedDiagnosticReportProviderOrganizationId')}>
                        <FormLabel isRequired>{translate.request.selectGrader}</FormLabel>

                        <InputContainer>
                            {organizationPractitioners && !noGradersOnMount && (
                                <>
                                    <Async<IReactSelectOptions>
                                        className={classnames('select-form-control', 'automation-graderSelector')}
                                        classNamePrefix="Select"
                                        components={{ Option: SelectOption }}
                                        defaultOptions
                                        isDisabled={graderSelectedByRequester && !props.isExaminationRequest}
                                        key={`${GraderSelector}-reload`} // refreshToken will be added in a newer version, does not exist in 1.7.12
                                        loadOptions={async (input: string) => getOptions(input)}
                                        noOptionsMessage={() => translate.common.typeToSearch}
                                        onChange={async (newSelectedOption) => {
                                            if (newSelectedOption && !Array.isArray(newSelectedOption)) {
                                                await handleChange(newSelectedOption);
                                            }
                                        }}
                                        placeholder={translate.request.selectGrader}
                                        value={selectedOption}
                                    />
                                    <Feedback
                                        error={props.errors.get('selectedDiagnosticReportProviderOrganizationId')}
                                    />
                                </>
                            )}
                        </InputContainer>
                    </HorizontalFormGroup>

                    {noGradersOnMount && <Alert appearance="warning">{translate.request.noProvidersForRequest}</Alert>}

                    {graderSelectedByRequester && !props.isExaminationRequest && (
                        <Alert appearance="info">{translate.request.requesterSelectedRouting}</Alert>
                    )}
                </>
            )}
        </>
    );

    async function handleChange(newSelectedOption?: IReactSelectOptions) {
        setSelectedOption(newSelectedOption);

        const organizationPractitionerIds = newSelectedOption?.value || null;

        let selectedOrganizationId: string | null = null;
        let selectedPractitionerId: string | null = null;
        if (organizationPractitionerIds) {
            if (organizationPractitionerIds.includes('_')) {
                selectedOrganizationId = organizationPractitionerIds.split('_')[0];
                selectedPractitionerId = organizationPractitionerIds.split('_')[1];
            } else {
                selectedOrganizationId = organizationPractitionerIds;
            }
        }

        await onChange(selectedOrganizationId, selectedPractitionerId);
        if (
            selectedOrganizationId !==
            props.careProviderAndRequesterAndSelectedProvider.selectedDiagnosticReportProviderOrganizationId
        ) {
            await props.validateOne('selectedDiagnosticReportProviderOrganizationId');
        }
    }

    async function reloadOptions() {
        await loadOrganizations(props.service.id, false);

        const hasSelectedOptionDisappeared =
            selectedOption &&
            !organizationPractitioners?.some((organizationOption) =>
                organizationOption.practitioners.some(
                    (practitionerOption) => practitionerOption.value === selectedOption.value
                )
            );

        if (hasSelectedOptionDisappeared) {
            await handleChange();
        }
    }

    async function getOptions(input: string) {
        await loadOrganizations(props.service.id, false, input);
        const options: { className?: string; isDisabled: boolean; label: string; value: string }[] = [];
        if (organizationPractitioners) {
            organizationPractitioners.forEach((organization) => {
                const classNameOrganization = organization.disabled
                    ? 'Select__group-header is-disabled'
                    : 'Select__group-header';
                options.push({
                    className: classNameOrganization,
                    isDisabled: organization.disabled,
                    label: organization.label,
                    value: organization.value,
                });
                for (const practitioner of organization.practitioners) {
                    const classNamePractitioner = practitioner.disabled
                        ? 'Select__group-option is-disabled'
                        : 'Select__group-option';
                    options.push({
                        className: classNamePractitioner,
                        isDisabled: practitioner.disabled,
                        label: practitioner.label,
                        value: practitioner.value,
                    });
                }
            });
        }

        return options;
    }

    async function onChange(selectedOrganizationId: string | null, selectedPractitionerId: string | null) {
        await props.handleChange('selectedDiagnosticReportProviderOrganizationId', selectedOrganizationId);
        await props.handleChange('selectedDiagnosticReportProviderPractitionerId', selectedPractitionerId);
    }

    async function loadOrganizations(serviceId: string, initialMount: boolean, searchInput?: string) {
        const urlBuilder = new UrlBuilder(`/api/services/${serviceId}/serviceProviders/graders`).add(
            'procedurePerformerOrganizationId',
            props.procedurePerformerOrganizationId
        );

        if (props.careProviderAndRequesterAndSelectedProvider.requesterOrganizationId) {
            urlBuilder.add(
                'requesterOrganizationId',
                props.careProviderAndRequesterAndSelectedProvider.requesterOrganizationId
            );
        } else if (props.careProviderAndRequesterAndSelectedProvider.draftCareProviderOrganizationId) {
            urlBuilder.add(
                'requesterOrganizationId',
                props.careProviderAndRequesterAndSelectedProvider.draftCareProviderOrganizationId
            );
        }

        if (searchInput) {
            urlBuilder.add('searchText', searchInput);
        }

        const url = urlBuilder.build();
        const result = await fetchJson(url, 'GET');

        const organizationPractitionersOptions: IOrganizationPractitionersOptions[] = [];
        for (const organization of result.json.items as IServiceProviderOrganizationPractitioners[]) {
            const practitionerOptions: { disabled: boolean; isAvailable: boolean; label: string; value: string }[] = [];
            for (const practitioner of organization.practitioners) {
                const isAvailable = practitioner.absenceEndDate === null && practitioner.isActivated;

                practitionerOptions.push({
                    disabled: !isAvailable,
                    isAvailable,
                    label: createPractitionerLabel(practitioner),
                    value: `${organization.id}_${practitioner.id}`,
                });
            }

            const hasAvailablePractitioners = practitionerOptions.some((practitioner) => practitioner.isAvailable);
            organizationPractitionersOptions.push({
                disabled: !organization.isOrganizationRequestRecipient || !hasAvailablePractitioners,
                label: createOrganizationLabel(organization),
                practitioners: practitionerOptions,
                value: organization.id,
            });
        }

        setOrganizationPractitioners(organizationPractitionersOptions);

        if (initialMount) {
            await initializeSelectedOption(organizationPractitionersOptions);
            if (organizationPractitionersOptions.length === 0) {
                setNoGradersOnMount(true);
            }
        }
    }

    async function initializeSelectedOption(organizationPractitionersOptions: IOrganizationPractitionersOptions[]) {
        let initializeOrganizationId =
            props.process?.selectedDiagnosticReportProviderOrganizationId ??
            props.careProviderAndRequesterAndSelectedProvider.selectedDiagnosticReportProviderOrganizationId;
        let initializePractitionerId =
            props.process?.selectedDiagnosticReportProviderPractitionerId ??
            props.careProviderAndRequesterAndSelectedProvider.selectedDiagnosticReportProviderPractitionerId;

        const initializeLoggedInPractitionerAsDefault =
            !initializeOrganizationId && !initializePractitionerId && props.service.userIsGrader;
        if (initializeLoggedInPractitionerAsDefault) {
            initializeOrganizationId = organizationId;
            initializePractitionerId = practitionerId;
        }

        if (organizationPractitionersOptions.length > 0 && initializeOrganizationId) {
            let value = initializeOrganizationId;
            let intialSelectedOption: { label: string; value: string } | undefined =
                organizationPractitionersOptions.find((item) => item.value === value);

            if (initializePractitionerId) {
                value += '_' + initializePractitionerId;
                const selectedOrganization = filter(organizationPractitionersOptions, { practitioners: [{ value }] });
                intialSelectedOption = selectedOrganization[0]?.practitioners.find((item) => item.value === value);
            }

            if (intialSelectedOption) {
                setSelectedOption(intialSelectedOption);
                await onChange(initializeOrganizationId, initializePractitionerId);
                await props.validateOne('selectedDiagnosticReportProviderOrganizationId');
            } else {
                await onChange(null, null);
            }
        } else {
            await onChange(null, null);
        }
    }

    function createPractitionerLabel(practitioner: IPractitioner) {
        let label = getDisplayName(practitioner);
        if (practitioner.absenceEndDate) {
            label += ` (${translate.request.absentUpToAndIncluding} ${DateTime.from(practitioner.absenceEndDate).format(
                'DD-MM-YYYY'
            )})`;
        }

        return label;
    }

    function createOrganizationLabel(organization: IServiceProviderOrganizationPractitioners) {
        return `${organization.name}${organization.city ? `, ${organization.city}` : ''}`;
    }
}
